import type { OnboardingTaxonomyOption } from '../types';

interface TaxonomyCheckboxGroupProps {
  label: string;
  options: OnboardingTaxonomyOption[];
  /** Currently selected option ids. */
  selected: string[];
  onToggle: (id: string) => void;
  error?: string;
}

/**
 * Multi-select taxonomy picker for the sign-up form (PE-2097).
 *
 * ⚠️ Module scope, not nested in SignUpPage — see OnboardingShell for why a component
 * declared inside another component's body remounts its whole subtree on every render
 * (PE-2272 bug 1). The selection state stays in the page; this takes it as props.
 */
export const TaxonomyCheckboxGroup = ({
  label,
  options,
  selected,
  onToggle,
  error,
}: TaxonomyCheckboxGroupProps) => (
  <fieldset className="space-y-2">
    <legend className="text-[14px] font-[600] text-foreground">{label}</legend>
    {/* Checkboxes rather than a multi-select: both fields are @ArrayMinSize(1)
        with no upper bound, and a native multi-select hides how many are picked. */}
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const checked = selected.includes(option.id);
        return (
          <label
            key={option.id}
            className={`text-sm px-3 py-1.5 rounded-full border cursor-pointer transition-colors ${
              checked
                ? 'bg-foreground text-background border-foreground'
                : 'border-border text-foreground hover:bg-muted'
            }`}>
            <input
              type="checkbox"
              className="sr-only"
              checked={checked}
              onChange={() => onToggle(option.id)}
            />
            {option.name}
          </label>
        );
      })}
    </div>
    {error && <p className="text-xs text-destructive">{error}</p>}
  </fieldset>
);
